'use client';
import React, { useEffect, useMemo, useState } from 'react';
import camelcaseKeys from 'camelcase-keys';
import classNames from 'classnames';

import { TrackingManager } from '@benzinga/tracking-manager';
import styles from './PartialArticleLayout.module.scss';

import {
  authorTypeGenerator,
  calculateReadTime,
  generatePartialViewPreviewFromArticleBlocks,
  getCanonicalUrl,
  printProHeadlineArticleContent,
  setDFPTargeting,
} from '@benzinga/article-manager';

import { Button, ButtonVariant, ErrorBoundary, Icon, Layout } from '@benzinga/core-ui';

import { checkDeviceType } from '@benzinga/device-utils';
import { NoFirstRender, useHydrate } from '@benzinga/hooks';
import { VisiblePageTracker } from '@benzinga/analytics';

import { NodeLayout } from '@benzinga/content-manager';
import { ArticleBlock, ArticleData, ArticleManager, getPrimaryTickers } from '@benzinga/article-manager';

import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { sanitizeHTML } from '@benzinga/frontend-utils';
//import { ArticleLayoutSidebar } from './ArticleLayoutSidebar';
import { SessionContext } from '@benzinga/session-context';

import { ShareButtons } from '@benzinga/ui';
import { ArticlePageContext } from '../context';
import { AdvertiserDisclosure } from './AdvertiserDisclosure';
import AppleNewsButton from './AppleNewsButton';
import { ArticleBlocks } from './ArticleBlocks';
import { ArticleLayoutWrapper, ArticleContentWrapper } from './ArticleLayoutChrome';
import { AuthorContent } from './AuthorContent';
import GoogleNewsButton from './GoogleNewsButton';
import { ImagePlacement } from './ImagePlacement';
import KeyPoints from './KeyPoints';
import ReadInAppButton from './ReadInAppButton';

interface PartialArticleDataProps {
  articleID?: number;
  articleData: ArticleData;
  deviceType?: string | null;
  articleScrollViewMoreLink?: string;
  googleNewsUrlKey?: string;
  lastStory?: boolean;
  layout?: NodeLayout;
  loadMoreButtonVariant?: ButtonVariant;
  showAdvertiserDisclosure?: boolean;
  executeImpressionEvent?: boolean;
  executeReplaceHistory?: boolean;
  executePageviewEvent?: boolean;
  showFontAwesomeIcons?: boolean;
  showWhatsAppIcon?: boolean;
  trackMeta?: boolean;
}

type MinimumRequiredArticleProps = Required<
  Pick<PartialArticleDataProps, 'articleData'> | Pick<PartialArticleDataProps, 'articleID'>
>;

export const PartialArticleLayout = ({
  articleData: initialArticleData,
  articleID,
  articleScrollViewMoreLink,
  deviceType,
  executeImpressionEvent,
  executePageviewEvent,
  executeReplaceHistory,
  googleNewsUrlKey,
  lastStory,
  //layout,
  loadMoreButtonVariant,
  showFontAwesomeIcons,
  showWhatsAppIcon,
  trackMeta = true,
}: MinimumRequiredArticleProps & PartialArticleDataProps) => {
  const [articleData, setArticleData] = useState(initialArticleData);
  const articleMeta = camelcaseKeys(articleData?.meta, { deep: true });
  const showAdvertiserDisclosure = articleMeta?.flags?.showAdvertiserDisclosure || false;
  const session = React.useContext(SessionContext);
  const articleUrl = getCanonicalUrl(articleData ?? {});

  useEffect(() => {
    if (!articleData && articleID) {
      const articleManager = session.getManager(ArticleManager);
      articleManager.getArticle(articleID).then(article => {
        article.ok && setArticleData(article.ok);
      });
    }
  }, [articleData, articleID, session]);

  if (!articleData) {
    return null;
  }

  return (
    <ArticlePageContext.Provider value={{ campaign: null, campaignStrategy: null }}>
      <ArticleLayoutWrapper className="article-layout-wrapper">
        <Layout
          layoutMain={
            <PartialArticleLayoutMain
              {...{
                articleData,
                articleScrollViewMoreLink,
                deviceType,
                executeImpressionEvent,
                executePageviewEvent,
                executeReplaceHistory,
                googleNewsUrlKey,
                lastStory,
                loadMoreButtonVariant,
                showAdvertiserDisclosure,
                showFontAwesomeIcons,
                showWhatsAppIcon,
                trackMeta,
              }}
            />
          }
          layoutMainClassName="w-full overflow-hidden"
          // layoutSidebar={
          //   <ArticleLayoutSidebar
          //     {...{
          //       article: articleData,
          //       articleIndex,
          //       isHeadline: articleData?.isHeadline,
          //       layout,
          //       nodeId: articleData?.nodeId,
          //       partialView: true,
          //     }}
          //   />
          // }
          title={articleData.title}
          url={articleUrl}
        />
      </ArticleLayoutWrapper>
    </ArticlePageContext.Provider>
  );
};

const PartialArticleLayoutMain: React.FC<PartialArticleDataProps> = ({
  articleData,
  articleScrollViewMoreLink = 'top-stories',
  deviceType,
  executeImpressionEvent = false,
  executePageviewEvent = false,
  executeReplaceHistory = false,
  googleNewsUrlKey,
  lastStory,
  layout,
  loadMoreButtonVariant = 'flat-light-blue',
  showAdvertiserDisclosure = false,
  showFontAwesomeIcons,
  showWhatsAppIcon,
  trackMeta = true,
}) => {
  const session = React.useContext(SessionContext);
  const isMobile = useHydrate(checkDeviceType().isMobile(), false);
  const isApple = useHydrate(checkDeviceType().isApple(), false);
  const createdAt = articleData?.createdAt ? new Date(articleData.createdAt).toISOString() : null;
  const contentType = articleData?.type;
  const keyItems = articleData?.keyItems && articleData?.keyItems?.map(item => item.value).filter(item => item?.length);
  const title = articleData?.title;

  const articleUrl = getCanonicalUrl(articleData ?? {});

  const tickers = getPrimaryTickers(articleData?.tickers);

  const previewText = useMemo(() => {
    const result = generatePartialViewPreviewFromArticleBlocks(articleData?.blocks as ArticleBlock[]);
    return result || articleData?.teaserText || articleData?.body;
  }, [articleData?.blocks, articleData?.body, articleData?.teaserText]);

  const handleReadMore = () => {
    if (showAdvertiserDisclosure) {
      session.getManager(TrackingManager).trackCampaignEvent('view', {
        partner_id: (articleData?.meta?.Reach?.Disclosure?.tid || 0).toString(),
        unit_type: 'infinite-scroll',
      });
      session.getManager(TrackingManager).trackPageEvent('load_more', {});
    }
  };

  const onArticleVisible = articleData => {
    setDFPTargeting(articleData);
  };

  const authorName =
    articleData?.author?.firstname && articleData?.author?.lastname
      ? `${articleData?.author.firstname} ${articleData?.author.lastname}`
      : articleData?.author?.name || articleData?.name;

  if (!articleData) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col w-full items-start lg:flex-row">
        <ErrorBoundary name="partial-article-layout-main-author-content">
          <AuthorContent
            authorLink={articleData?.author?.profileUrl}
            authorName={authorName ?? ''}
            authorType={authorTypeGenerator(
              articleData?.author?.byLine ?? '',
              showAdvertiserDisclosure,
              contentType ?? '',
            )}
            className="flex flex-grow"
            created={createdAt ?? ''}
            isDraft={false}
            readTime={calculateReadTime(articleData.body)}
            twitter={articleData.author?.twitter}
          />
        </ErrorBoundary>
        <div className="flex items-center lg:ml-auto flex-wrap gap-2">
          <ShareButtons
            buttonLocation="article_page"
            className="flex"
            showFontAwesomeIcons={showFontAwesomeIcons}
            showWhatsAppIcon={showWhatsAppIcon}
            tickers={tickers}
            title={title}
            url={articleUrl}
          />
          <NoFirstRender fallback={<div className="h-[40px] w-[128px]" />}>
            {googleNewsUrlKey === 'benzingaIndia' ? (
              <GoogleNewsButton urlKey={googleNewsUrlKey} />
            ) : !showAdvertiserDisclosure && isApple ? (
              <AppleNewsButton />
            ) : deviceType === 'mobile' ? (
              <ReadInAppButton />
            ) : (
              <GoogleNewsButton />
            )}
          </NoFirstRender>
        </div>
      </div>

      {!!showAdvertiserDisclosure && <AdvertiserDisclosure articleData={articleData} slug={layout?.disclosure_slug} />}

      <ImagePlacement
        caption={articleData?.assets?.source}
        primaryImage={articleData.primaryImage || { url: '/next-assets/images/benzinga-post-card-placeholder.svg' }}
        title={articleData?.title}
      />

      <ArticleContentWrapper
        className={classNames('article-content-body', { 'is-headline': articleData?.isHeadline })}
        id={`node-${articleData.nodeId}`}
      >
        {Array.isArray(layout?.content_header?.blocks) && <ArticleBlocks blocks={layout.content_header.blocks} />}

        <ErrorBoundary name="partial-article-layout-main-visible-page-tracker">
          <VisiblePageTracker
            article={articleData}
            executeImpressionEvent={executeImpressionEvent}
            executePageviewEvent={executePageviewEvent}
            executeReplaceHistory={executeReplaceHistory}
            meta={articleData.metaProps}
            onImpress={() => onArticleVisible(articleData)}
            trackMeta={trackMeta}
          >
            <div style={{ height: 2 }} />
          </VisiblePageTracker>
        </ErrorBoundary>

        <div className={styles.previewTextWrapper}>
          {articleData?.isHeadline ? (
            <div className="" dangerouslySetInnerHTML={{ __html: sanitizeHTML(printProHeadlineArticleContent()) }} />
          ) : (
            <div className="preview-text" dangerouslySetInnerHTML={{ __html: sanitizeHTML(previewText ?? '') }} />
          )}
        </div>

        <React.Suspense fallback={<div />}>
          <div className="read-more-box">
            <Button as="a" href={articleUrl} onClick={handleReadMore} variant={loadMoreButtonVariant}>
              Read More
              <Icon className="chevron-right-icon" icon={faChevronRight} />
            </Button>
            <ShareButtons
              buttonLocation="article_page"
              className="flex"
              showFontAwesomeIcons={showFontAwesomeIcons}
              showWhatsAppIcon={showWhatsAppIcon}
              tickers={tickers}
              title={title}
              url={articleUrl}
            />
          </div>
        </React.Suspense>

        <hr />

        <ErrorBoundary name="partial-article-layout-main-visible-page-tracker">
          <VisiblePageTracker
            article={articleData}
            executeImpressionEvent={false}
            executePageviewEvent={false}
            meta={articleData.metaProps}
            trackMeta={trackMeta}
          >
            <div />
          </VisiblePageTracker>
        </ErrorBoundary>

        {lastStory && (
          <div className={styles.viewMoreWrapper}>
            <Button
              as="a"
              className="top-stories-button"
              href={`/${articleScrollViewMoreLink}`}
              size="lg"
              variant="primary"
            >
              View More Top Stories
            </Button>
          </div>
        )}
      </ArticleContentWrapper>
    </>
  );
};
